import React, { createContext, ReactNode, useContext, useMemo } from 'react';

/**
 * Consignment-level facts every analytics event fired beneath an `AnalyticsScope` should carry.
 * Read-only: the page that knows them supplies them once, and any descendant's `useAnalytics()`
 * picks them up. Outside a scope the value is empty and events simply omit these fields.
 */
export interface AnalyticsScopeValue {
    /** Whether the signed-in account is the receiver of the consignment this part of the app is about. */
    isReceiver?: boolean;
}

const AnalyticsScopeContext = createContext<AnalyticsScopeValue>({});

/** The nearest `AnalyticsScope`'s value, or an empty object when there is none above the caller. */
export const useAnalyticsScope = (): AnalyticsScopeValue => useContext(AnalyticsScopeContext);

interface AnalyticsScopeProps extends AnalyticsScopeValue {
    children: ReactNode;
}

/**
 * Supplies consignment-level context to every `useAnalytics()` call in its subtree, so events fired
 * from deep children carry `user_is_receiver` without the analytics hook reading movement state.
 * Scopes nest: an inner value wins, and a prop left `undefined` inherits the outer scope's value.
 */
export const AnalyticsScope: React.FC<AnalyticsScopeProps> = ({ isReceiver, children }) => {
    const outer = useAnalyticsScope();
    const value = useMemo(() => (isReceiver === undefined ? outer : { ...outer, isReceiver }), [outer, isReceiver]);

    return <AnalyticsScopeContext.Provider value={value}>{children}</AnalyticsScopeContext.Provider>;
};
